import api from './api'

export interface Supplier {
  supplier_id: number
  supplier_code?: string
  company_name: string
  contact_person?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  state_province?: string
  postal_code?: string
  country?: string
  tax_number?: string
  default_currency_code?: string
  payment_terms_days?: number
  is_active?: boolean
  notes?: string
  created_at?: string
  updated_at?: string
  currencies?: {
    currency_code: string
    currency_name: string
    symbol: string
    exchange_rate: number
  }
  _count?: {
    purchases: number
    supplier_price_lists: number
  }
  purchases?: {
    purchase_id: number
    purchase_number: string
    purchase_date: string
    total_amount: number
    status: string
    currency_code: string
    _count?: {
      purchase_items: number
    }
  }[]
}

export interface SupplierContact {
  contact_id?: number
  supplier_id?: number
  contact_name: string
  position?: string
  email?: string
  phone?: string
  is_primary?: boolean
}

export interface SupplierPriceList {
  list_id: number
  supplier_id: number
  list_name?: string
  effective_date: string
  currency_code: string
  notes?: string
  created_at?: string
  supplier_price_list_items: SupplierPriceListItem[]
  currencies?: {
    symbol: string
  }
}

export interface SupplierPriceListItem {
  item_id: number
  list_id: number
  product_id: number
  unit_price: number
  minimum_quantity?: number
  lead_time_days?: number
  notes?: string
  products?: {
    product_name: string
    sku: string
  }
}

export interface ProductPriceHistory {
  type: 'purchase' | 'price_list'
  date: string
  reference: string
  unit_price: number
  unit_price_base?: number
  currency_code?: string
  exchange_rate?: number
  quantity?: number
  landed_cost?: number
  shipping_cost_allocated?: number
  suggested_retail_price?: number
  margin_percent?: number
  minimum_quantity?: number
  lead_time_days?: number
}

export const supplierService = {
  // Basic CRUD
  getAll: (params?: { 
    search?: string; 
    page?: number; 
    limit?: number;
    country?: string;
    is_active?: boolean;
  }) =>
    api.get('/suppliers', { params }),

  getById: (id: number | string) =>
    api.get(`/suppliers/${id}`),

  create: (data: Partial<Supplier> & { contacts?: SupplierContact[] }) =>
    api.post('/suppliers', data),

  update: (id: number | string, data: Partial<Supplier> & { contacts?: SupplierContact[] }) =>
    api.put(`/suppliers/${id}`, data),

  delete: (id: number | string) =>
    api.delete(`/suppliers/${id}`),

  // Countries
  getCountries: () =>
    api.get('/suppliers/countries/list'),

  // Products and Pricing
  getProducts: (id: number | string) =>
    api.get(`/suppliers/${id}/products`),

  getProductPriceHistory: (id: number | string, productId: number | string) =>
    api.get(`/suppliers/${id}/products/${productId}/price-history`),

  // Price Lists
  getPriceLists: (id: number | string) =>
    api.get(`/suppliers/${id}/price-lists`),

  createPriceList: (id: number | string, data: {
    list_name?: string
    effective_date: string
    currency_code?: string
    notes?: string
    items: {
      product_id: number
      unit_price: number
      minimum_quantity?: number
      lead_time_days?: number
      notes?: string
    }[]
  }) =>
    api.post(`/suppliers/${id}/price-lists`, data),

  // Stats
  getStats: (id: number | string) =>
    api.get(`/suppliers/${id}/stats`),
}
